import { useEffect, useState } from "react";
import Map, { Marker } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
    ArrowUpRight, Building2, ChevronDown, ChevronRight, ClipboardList, Globe, Landmark, ListPlus, Mail, MapPin, Navigation,
    Network, Phone, PhoneCall, Plus,
} from "lucide-react";
import { IEstablishment } from "../../../interfaces/establishment";
import { EstablishmentMyContext } from "../../../interfaces/establishments";
import { updateProspect } from "../../../interfaces/prospect";
import { AddToListPicker } from "../../../components/prospection/AddToListPicker";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Select } from "../../../components/ui/Select";
import { FounderCounts } from "../../../lib/founderCounts";
import { addressLines, fmtDate, mapsLinks, Person, PROSPECT_STATUSES, relativeFr } from "./model";
import { ContactLine, CopyButton, Field, Section } from "./parts";

const MAPBOX_TOKEN =
    process.env.REACT_APP_MAPBOX_TOKEN ||
    "pk.eyJ1IjoibWF0aGlldWR1Y2tmbG93IiwiYSI6ImNtajc5cnQ1aDAxeGEzcHNhZWtub3I5N3QifQ.BaEcgEbT0TKEYgeBX0KwVw";

const fieldClass =
    "rounded-lg border border-border bg-surface px-3 py-2 font-sans text-sm text-ink placeholder:text-ink-faint focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

/* ─── Mon suivi ─────────────────────────────────────────────────────────── */

type Prospect = NonNullable<EstablishmentMyContext["prospect"]>;

export function FollowUp({ establishmentId, context, crmBusy, onAddToCrm, onProspectChange, onListAdded }: {
    establishmentId: string;
    context: EstablishmentMyContext | null;
    crmBusy: boolean;
    onAddToCrm: () => void;
    onProspectChange: (p: Prospect) => void;
    onListAdded: (list: { _id: string; name: string }) => void;
}) {
    const prospect = context?.prospect;
    const lists = context?.lists || [];

    return (
        <Section id="suivi" title="Mon suivi" icon={ClipboardList} dense
            action={prospect && (
                <a href={`/kanban?openProspect=${prospect._id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-sans text-xs font-medium text-ink-muted hover:text-ink">
                    CRM <ArrowUpRight className="h-3 w-3" />
                </a>
            )}
        >
            {!context ? (
                <div className="h-24 animate-pulse rounded-lg bg-surface-sunken motion-reduce:animate-none" />
            ) : prospect ? (
                <ProspectEditor key={prospect._id} prospect={prospect} onChange={onProspectChange} />
            ) : (
                <div className="space-y-3">
                    <p className="font-sans text-sm text-ink-muted">Pas encore dans votre CRM. Suivez-le pour noter vos échanges et votre prochaine action.</p>
                    <Button variant="secondary" size="sm" className="w-full" onClick={onAddToCrm} disabled={crmBusy}>
                        <Plus className="h-4 w-4" />
                        Suivre dans mon CRM
                    </Button>
                </div>
            )}

            <div className="mt-4 border-t border-border pt-4">
                <p className="mb-2 font-sans text-xs text-ink-muted">Listes de prospection</p>
                <div className="flex flex-wrap items-center gap-1.5">
                    {lists.map((l) => (
                        <a key={l._id} href={`/prospection/${l._id}`} className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                            <Badge className="max-w-48 hover:border-border-strong"><span className="truncate">{l.name}</span></Badge>
                        </a>
                    ))}
                    <AddToListPicker establishmentIds={[establishmentId]} onAdded={onListAdded}>
                        {({ onClick }) => (
                            <button type="button" onClick={onClick} className="inline-flex items-center gap-1 rounded-full px-2 py-1 font-sans text-xs font-medium text-ink-muted transition-colors duration-150 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                                <ListPlus className="h-3.5 w-3.5" />
                                {lists.length ? "Ajouter" : "Ajouter à une liste"}
                            </button>
                        )}
                    </AddToListPicker>
                </div>
            </div>
        </Section>
    );
}

/** Today + n working days (Mon–Fri), as the yyyy-mm-dd a date input expects. */
function workingDaysFromNow(n: number): string {
    const d = new Date();
    while (n > 0) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) n--;
    }
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function ProspectEditor({ prospect, onChange }: { prospect: Prospect; onChange: (p: Prospect) => void }) {
    const [notes, setNotes] = useState(prospect.notes || "");
    const [nextAction, setNextAction] = useState(prospect.nextAction || "");
    // No date yet → relance in 3 working days, written with the prospect's next save.
    const [nextAt, setNextAt] = useState(prospect.nextActionAt ? prospect.nextActionAt.slice(0, 10) : workingDaysFromNow(3));
    const [saving, setSaving] = useState(false);

    useEffect(() => { setNotes(prospect.notes || ""); }, [prospect.notes]);

    const save = async (patch: Partial<Prospect>) => {
        if (!prospect.nextActionAt && nextAt && !("nextActionAt" in patch)) patch = { ...patch, nextActionAt: new Date(nextAt).toISOString() };
        setSaving(true);
        const updated = await updateProspect(prospect._id, patch);
        setSaving(false);
        if (updated?._id) onChange({ ...prospect, ...patch });
    };

    const lastContact = prospect.lastContactAt ? new Date(prospect.lastContactAt) : null;
    const due = prospect.nextActionAt ? new Date(prospect.nextActionAt) : null;
    const overdue = !!due && due.getTime() < Date.now() - 86400000;

    return (
        <div className="space-y-3">
            <Select fullWidth value={prospect.status} options={PROSPECT_STATUSES} onChange={(status) => save({ status })} disabled={saving} />

            <div className="flex items-center justify-between gap-2">
                <p className="font-sans text-xs text-ink-muted">
                    {lastContact ? `Dernier contact ${relativeFr(lastContact)}` : "Jamais contacté"}
                </p>
                <Button
                    variant="ghost"
                    size="sm"
                    disabled={saving}
                    onClick={() => save({ lastContactAt: new Date().toISOString(), ...(prospect.status === "new" ? { status: "contacted" } : {}) })}
                >
                    <PhoneCall className="h-4 w-4" />
                    J'ai appelé
                </Button>
            </div>

            <div>
                <label className="mb-1.5 block font-sans text-xs text-ink-muted" htmlFor="next-action">
                    Prochaine action
                    {overdue && <span className="ml-1.5 font-medium text-danger">· en retard</span>}
                </label>
                <div className="space-y-2">
                    <input
                        id="next-action"
                        value={nextAction}
                        onChange={(e) => setNextAction(e.target.value)}
                        onBlur={() => nextAction !== (prospect.nextAction || "") && save({ nextAction })}
                        placeholder="Rappeler, envoyer l'offre…"
                        className={`${fieldClass} w-full`}
                    />
                    <input
                        type="date"
                        value={nextAt}
                        aria-label="Date de la prochaine action"
                        onChange={(e) => {
                            setNextAt(e.target.value);
                            save({ nextActionAt: e.target.value ? new Date(e.target.value).toISOString() : (null as any) });
                        }}
                        className={`${fieldClass} w-full`}
                    />
                </div>
            </div>

            <div>
                <label className="mb-1.5 block font-sans text-xs text-ink-muted" htmlFor="prospect-notes">Notes</label>
                <textarea
                    id="prospect-notes"
                    value={notes}
                    rows={3}
                    onChange={(e) => setNotes(e.target.value)}
                    onBlur={() => notes !== (prospect.notes || "") && save({ notes })}
                    placeholder="Ce qui a été dit, objections, interlocuteur…"
                    className={`${fieldClass} block w-full resize-y`}
                />
            </div>
        </div>
    );
}

/* ─── Coordonnées ───────────────────────────────────────────────────────── */

export function Coordinates({ establishment: e }: { establishment: IEstablishment }) {
    const { line1, line2, full } = addressLines(e);
    const maps = mapsLinks(e);
    const geo = e.address?.geo?.coordinates;
    const socials = (e.social_networks || []).filter((s) => s.url);
    const site = e.website_url?.replace(/^https?:\/\/(www\.)?/, "").split("/")[0];

    return (
        <Section title="Coordonnées" icon={Phone} dense>
            <div className="space-y-1">
                {e.phone ? <ContactLine icon={Phone} href={`tel:${e.phone}`} value={e.phone} /> : <p className="font-sans text-sm text-ink-faint">Pas de standard connu</p>}
                {e.email && <ContactLine icon={Mail} href={`mailto:${e.email}`} value={e.email} />}
                {e.website_url && site && <ContactLine icon={Globe} href={e.website_url} value={site} copy={false} external />}
            </div>

            {socials.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                    {socials.map((s) => {
                        const followers = s.follower_count?.length ? s.follower_count[s.follower_count.length - 1].count : null;
                        return (
                            <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer" className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                                <Badge className="capitalize hover:border-border-strong">
                                    {s.platform}
                                    {followers !== null && <span className="font-mono normal-case text-ink-faint">{followers.toLocaleString("fr-FR")}</span>}
                                </Badge>
                            </a>
                        );
                    })}
                </div>
            )}

            <div className="mt-4 border-t border-border pt-4">
                {geo && geo.length === 2 && (
                    <div className="mb-3 h-36 overflow-hidden rounded-lg border border-border">
                        <Map
                            initialViewState={{ latitude: geo[1], longitude: geo[0], zoom: 15 }}
                            mapStyle="mapbox://styles/mapbox/light-v11"
                            mapboxAccessToken={MAPBOX_TOKEN}
                            interactive={false}
                            attributionControl={false}
                            style={{ width: "100%", height: "100%" }}
                        >
                            <Marker latitude={geo[1]} longitude={geo[0]} anchor="bottom">
                                <MapPin className="h-6 w-6 fill-accent text-surface" />
                            </Marker>
                        </Map>
                    </div>
                )}
                <div className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ink-faint" />
                    <div className="min-w-0 flex-1 font-sans text-sm">
                        {line1 && <p className="text-ink">{line1}</p>}
                        <p className="text-ink-muted">{line2 || "Adresse non renseignée"}</p>
                    </div>
                    {full && <CopyButton value={full} label="Copier l'adresse" />}
                </div>
                <div className="mt-3 flex gap-2">
                    <a href={maps.directions} target="_blank" rel="noopener noreferrer" className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg border border-border-strong font-sans text-sm font-medium text-ink transition-colors duration-150 hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                        <Navigation className="h-4 w-4" />
                        Itinéraire
                    </a>
                    <a href={maps.place} target="_blank" rel="noopener noreferrer" className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg font-sans text-sm font-medium text-ink transition-colors duration-150 hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                        Google Maps
                        <ArrowUpRight className="h-4 w-4 text-ink-faint" />
                    </a>
                </div>
            </div>
        </Section>
    );
}

/* ─── Réseau ────────────────────────────────────────────────────────────── */

export function NetworkCard({ establishment: e, people, counts }: { establishment: IEstablishment; people: Person[]; counts: Record<string, FounderCounts> }) {
    const franchise = (e as any).franchise as { _id: string; name: string } | null | undefined;
    const operators = people.filter((p) => p.name && (counts[p.id]?.active ?? 0) > 1);
    const siren = e.registration?.siren;
    if (!franchise?._id && operators.length === 0 && !siren && !e.brand_group) return null;

    const row = "flex items-center justify-between gap-2 rounded-lg px-2 py-2 -mx-2 font-sans text-sm text-ink transition-colors duration-150 hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";
    return (
        <Section title="Réseau" icon={Network} dense>
            <div className="space-y-0.5">
                {franchise?._id && (
                    <a href={`/franchiseurs/${franchise._id}`} className={row}>
                        <span className="min-w-0 truncate"><span className="text-ink-muted">Franchise </span>{franchise.name}</span>
                        <ArrowUpRight className="h-4 w-4 shrink-0 text-ink-faint" />
                    </a>
                )}
                {operators.map((p) => (
                    <a key={p.id} href={`/signals/founder?founderName=${encodeURIComponent(p.name)}`} className={row}>
                        <span className="min-w-0 truncate">
                            <span className="text-ink-muted">{p.name} · </span>{counts[p.id].active} établissements actifs
                        </span>
                        <ArrowUpRight className="h-4 w-4 shrink-0 text-ink-faint" />
                    </a>
                ))}
                {siren && (
                    <a href={`/siren/${siren}`} className={row}>
                        <span className="min-w-0 truncate">
                            <span className="text-ink-muted">Société · </span>
                            {e.registration?.is_siege === true ? "établissement siège" : e.registration?.is_siege === false ? "établissement secondaire" : "tous ses établissements"}
                        </span>
                        <ArrowUpRight className="h-4 w-4 shrink-0 text-ink-faint" />
                    </a>
                )}
                {e.brand_group && (
                    <p className="px-0 py-2 font-sans text-sm text-ink"><span className="text-ink-muted">Groupe </span>{e.brand_group}</p>
                )}
            </div>
        </Section>
    );
}

/* ─── Entreprise (repliée) ──────────────────────────────────────────────── */

export function Company({ establishment: e }: { establishment: IEstablishment }) {
    const [open, setOpen] = useState(false);
    const r = e.registration;
    const naf = [r?.naf_code, r?.naf_description].filter(Boolean).join(" · ");
    return (
        <section className="rounded-2xl border border-border bg-surface">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-3 rounded-2xl p-6 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
                <span className="flex items-center gap-2 font-sans text-base font-semibold text-ink">
                    <Landmark className="h-5 w-5 text-ink-faint" />
                    Entreprise
                    {e.legal_name && <span className="font-normal text-ink-muted">· {e.legal_name}</span>}
                </span>
                {open ? <ChevronDown className="h-5 w-5 text-ink-faint" /> : <ChevronRight className="h-5 w-5 text-ink-faint" />}
            </button>
            {open && (
                <div className="grid grid-cols-2 gap-x-6 gap-y-4 border-t border-border p-6 md:grid-cols-3">
                    <Field label="Raison sociale">{e.legal_name}</Field>
                    <Field label="Enseigne">{r?.enseigne && r.enseigne !== e.name ? r.enseigne : null}</Field>
                    <Field label="Sigle">{r?.sigle}</Field>
                    <Field label="SIREN">
                        {r?.siren && (
                            <a href={`/siren/${r.siren}`} className="inline-flex items-center gap-1 font-mono hover:underline">
                                {r.siren}
                                <Building2 className="h-3.5 w-3.5 text-ink-faint" />
                            </a>
                        )}
                    </Field>
                    <Field label="SIRET">
                        {r?.siret && (
                            <span className="inline-flex items-center gap-1 font-mono">
                                {r.siret}
                                <CopyButton value={r.siret} label="Copier le SIRET" />
                            </span>
                        )}
                    </Field>
                    <Field label="Forme juridique">{r?.legal_form}</Field>
                    <Field label="Code NAF">{naf || null}</Field>
                    <Field label="NAF de l'établissement">{r?.establishment_naf_code && r.establishment_naf_code !== r.naf_code ? r.establishment_naf_code : null}</Field>
                    <Field label="Création">{fmtDate(r?.creation_date, "long")}</Field>
                    <Field label="Type">{r?.is_siege === undefined ? null : r.is_siege ? "Siège" : "Établissement secondaire"}</Field>
                    <Field label="Taille de la société">{r?.company_size_category}</Field>
                    <Field label="Effectif de la société">{r?.workforce_range ? `${r.workforce_range}${r.workforce_year ? ` (${r.workforce_year})` : ""}` : null}</Field>
                    <Field label={`Population${e.address?.city ? ` · ${e.address.city}` : ""}`}>{e.cityStats?.population?.toLocaleString("fr-FR")}</Field>
                    <Field label="Prix médian commerce">{e.cityStats?.storesMedPricePerM2 ? `${Math.round(e.cityStats.storesMedPricePerM2).toLocaleString("fr-FR")} €/m²` : null}</Field>
                    <Field label="Mise à jour INSEE">{fmtDate(r?.last_status_update, "long")}</Field>
                </div>
            )}
        </section>
    );
}
